import { useCallback, useState } from 'react';
import { View } from 'react-native';

import {
  Button,
  EmailInput,
  FormControlTextInput,
  NameInput,
  PhoneNumberMaskInput,
  Text,
  XStack,
  createMqStyles,
} from '@fhs/ui';

import { AuthIdentifiers, useAuthV2Context } from '../context';
import { checkIdentifierValidity, formatPhoneNumber, isNameValid } from '../utils';

export function SignUp() {
  const styles = useMqStyles();

  const { getJwtIdentifier, signUp, loading } = useAuthV2Context();
  const { validatedIdentifiers } = getJwtIdentifier();

  const [name, setName] = useState<string>();
  const [email, setEmail] = useState<string>(validatedIdentifiers.emailAddress ?? '');
  const [phone, setPhone] = useState<string>(formatPhoneNumber(validatedIdentifiers.phoneNumber));
  const [emailError, setEmailError] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);

  const [dobMonth, setDobMonth] = useState<string>('Month');
  const [dobDay, setDobDay] = useState<string>('Day');
  const [dobYear, setDobYear] = useState<string>('Year');

  const onInputChangeName = useCallback(
    input => {
      setName(input);
      setNameError(false);
    },
    [setName, setNameError]
  );

  const onInputChangeEmail = useCallback(
    input => {
      setEmail(input);
      setEmailError(false);
    },
    [setEmail, setEmailError]
  );
  const onInputChangePhone = useCallback(
    input => {
      setPhone(input);
      setPhoneError(false);
    },
    [setPhone, setPhoneError]
  );

  const attemptSignUp = useCallback(async () => {
    const nameValid = isNameValid(name);
    const emailValid = checkIdentifierValidity({
      loginIdentifier: email,
      identifierType: AuthIdentifiers.EMAIL,
    });
    const phoneValid = checkIdentifierValidity({
      loginIdentifier: phone,
      identifierType: AuthIdentifiers.PHONE_NUMBER,
    });

    if (nameValid && emailValid && phoneValid) {
      await signUp({
        name,
        email,
        phoneNumber: phone,
        dob: `${dobYear}-${dobDay}-${dobMonth}`,
        wantsPromotionalEmail: false,
      });
    } else {
      setEmailError(!emailValid);
      setPhoneError(!phoneValid);
      setNameError(!nameValid);
    }
  }, [signUp, name, email, phone, dobDay, dobMonth, dobYear]);

  return (
    <View style={{ padding: 8 }}>
      <Text.Heading type={'three'}>Create an Account </Text.Heading>
      <NameInput value={name} onChange={onInputChangeName} error={nameError} />
      <EmailInput
        value={email}
        onChange={onInputChangeEmail}
        readOnly={!!validatedIdentifiers.emailAddress}
        error={emailError}
      />
      <PhoneNumberMaskInput
        value={phone}
        onChange={onInputChangePhone}
        readOnly={!!validatedIdentifiers.phoneNumber}
        error={phoneError}
      />
      <View style={styles.dobContainer}>
        <Text weight="semibold"> Date Of Birth (Optional)</Text>
        <XStack style={styles.dateContainer}>
          <FormControlTextInput
            placeholder={dobMonth}
            maxLength={2}
            keyboardType="numeric"
            onChangeText={setDobMonth}
            style={styles.dobText}
          />
          <FormControlTextInput
            placeholder={dobDay}
            maxLength={2}
            keyboardType="numeric"
            onChangeText={setDobDay}
            style={styles.dobText}
          />
          <FormControlTextInput
            maxLength={4}
            placeholder={dobYear}
            keyboardType="numeric"
            onChangeText={setDobYear}
            style={styles.dobText}
          />
        </XStack>
      </View>
      <View style={styles.continueButton}>
        <Button onPress={attemptSignUp} loading={loading} disabled={loading}>
          <Button.Text>Create Account</Button.Text>
        </Button>
      </View>
    </View>
  );
}

const useMqStyles = createMqStyles({
  dateContainer: {
    $base: {
      marginVertical: 6,
      gap: 12,
      width: '100%',
    },
  },
  dobContainer: {
    $base: {
      marginVertical: 12,
      width: '100%',
    },
  },
  dobText: {
    $base: {
      textAlign: 'center',
    },
  },
  continueButton: {
    $base: {
      width: '100%',
      height: 48,
    },
  },
});
